import { SupplyType, supplyTypeField } from "../../utils/booking/supplyType";
import { PaymentMethod, withPaymentMethod } from "../../utils/payment/paymentMethod";
import { getFlightSupplyType } from "../../utils/booking/flight/flightSupplyTypeStore";
import axios from "axios";

import toast from "react-hot-toast";

import { endPoints } from "../../constant/api_constants/url";
import { ENDPOINT } from "../../constant/environment";
import { API_METHODS } from "../../enums";
import { iCountryData, iCountryDataResponse } from "../../interface/common";
import { AirlineApiResponse, HotelBrandApiResponse } from "../../types";
import { decryptData, encryptData, getHeaderConfig } from "../../utils/api/encryption";
import { fetchApi } from "../../utils/apiFunction";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { getSharedByUserId } from "../../hooks/useIsSharedCartView";
import { getRequestedItineraryIdField, rememberRequestedItineraryId } from "../../utils/booking/requestedItineraryId";
import { getUserTimezoneField } from "../../utils/userTimezone";
import { getBrandTenantField } from "../../utils/booking/brandTenant";
import {
  getBookingIdempotencyKeyField,
  rememberBookingIdempotencyKey,
} from "../../utils/booking/bookingIdempotencyKey";
import { iHotelRoomBookArgs } from "../hotelDetailsAPI";
import { captureException } from "../../constant/sentryConfiguration";
import { hasUsableJWT } from "../../utils/authentication/hasUsableJWT";
import { recordCurrencyFromFlightSearch, recordCurrencyFromHotelSearch } from "../../utils/searchCurrency";
import { getPickSource } from "../../utils/booking/pickSourceStore";
import { isWidgetRuntime } from "../../utils/widgetRuntime";
import { MCP_WIDGET_TOOLS } from "../../constant/mcpWidget";
import { callWidgetTool, withoutUserId } from "../../widgets/mcp/widgetTools";

export const getAirports = async (
  accessJWT: string,
  requestBody: any,
  controller?: AbortController,
  newAirportUrl?: boolean
) => {
  const url = newAirportUrl ? `${ENDPOINT}/app/v1/flight/airportsPredictive` : `${ENDPOINT}/app/v1/flight/airports`;
  const config = getHeaderConfig(controller?.signal, accessJWT);
  if (!hasUsableJWT(accessJWT, "getAirports")) return null;
  try {
    consoleLog(["GET AIRPORTS PAYLOAD", requestBody]);
    const response = await axios.post(url, encryptData(requestBody), config);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["GET AIRPORTS RESPONSE", parseData]);
      if (parseData?.data) return parseData?.data;
      else return;
    } else return;
  } catch (error) {
    console.error("Error searching for airports:", error);
    throw error;
  }
};

export const getAirportsV2 = async (
  accessJWT: string,
  requestBody: any,
  controller?: AbortController,
  newAirportUrl?: boolean
) => {
  const url = newAirportUrl ? `${ENDPOINT}/app/v1/airportsPredictive` : `${ENDPOINT}/app/v1/airportSearch`;
  const body = {
    searchKey: requestBody,
  };
  const config = getHeaderConfig(controller?.signal, accessJWT);
  if (!hasUsableJWT(accessJWT, "getAirportsV2")) return null;
  try {
    consoleLog(["GET AIRPORTS PAYLOAD", requestBody]);
    const response = await axios.post(url, encryptData(body), config);
    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["GET AIRPORTS RESPONSE", parseData]);
      if (parseData?.data) return parseData?.data;
      else return;
    } else return;
  } catch (error) {
    console.error("Error searching for airports:", error);
    throw error;
  }
};

export const getFlightAvailability = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  fareId: string,
  userId: string
) => {
  const body = {
    version: "v2",
    id: `${encryptedKey}-${uniqueId}`,
    fareId: fareId,
    userId: userId,
    // The search's own supplyType, looked up by the encryptedKey this call is
    // for — so every caller is covered and no stale closure can send another
    // search's value.
    ...supplyTypeField(getFlightSupplyType(encryptedKey)),
  };
  return await fetchApi(accessJWT, endPoints.flightAvailability, API_METHODS.POST, body, null, controller);
};

export const getBaggageAndFareRulesForFlightSearch = async (
  accessJWT: string,
  requestBody: any,
  controller: AbortController
) => {
  const bodyWithVersion = {
    version: "v2",
    ...requestBody,
  };
  return await fetchApi(accessJWT, endPoints.fareRulesDetails, API_METHODS.POST, bodyWithVersion, null, controller);
};

export const getFlightDetails = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  fareId: string,
  userId: string
) => {
  const requestBody = {
    id: `${encryptedKey}-${uniqueId}`,
    fareId: fareId,
    userId: userId,
  };
  return await fetchApi(accessJWT, endPoints.flightDetails, API_METHODS.POST, requestBody, null, controller);
};

export const searchFlights = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  const bodyWithVersion = {
    version: "v3",
    ...requestBody,
  };
  const response = await fetchApi(accessJWT, endPoints.flightSearch, API_METHODS.POST, bodyWithVersion, null, controller);
  recordCurrencyFromFlightSearch(response);
  return response;
};

export const searchHotels = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  const bodyWithVersion = {
    version: "v2",
    ...requestBody,
  };
  const response = await fetchApi(accessJWT, endPoints.hotelSearch, API_METHODS.POST, bodyWithVersion, null, controller);
  recordCurrencyFromHotelSearch(response);
  return response;
};

// Hybrid Stays location search (ENABLE_HOTEL_SEARCH_GA_HYBRID, see
// gaEngine/hybrid.ts): app-backend runs the text searches, mode detection,
// Phase 2 and ranking on the autocomplete answers the browser already has.
// `lat`/`lng` are the user's approximate location (utils/geo/approxLocation.ts,
// the Radar IP lookup): Google biases text search to the caller, and the
// caller here is app-backend in Virginia. Null on any failure — the browser
// then runs the whole search itself. `meta` (timings and where the server got
// its answers) is typed in gaEngine/hybrid.ts's HybridRemoteResult.
export const getGAHotelSuggestions = async (
  accessJWT: string,
  requestBody: {
    q: string;
    acGeneral: unknown[];
    acGeo: unknown[];
    lat?: number;
    lng?: number;
  },
  controller?: AbortController
): Promise<{
  suggestions: any[];
  meta?: Record<string, any>;
} | null> => {
  const response = await fetchApi(accessJWT, endPoints.hotelGASuggestions, API_METHODS.POST, requestBody, null, controller);
  const data = response?.data;
  return data && Array.isArray(data.suggestions) ? data : null;
};

export const suggestHotelNames = async (
  accessJWT: string,
  requestBody: { city: string; country?: string; query: string; limit?: number },
  controller?: AbortController
) => {
  return await fetchApi(accessJWT, endPoints.hotelNameSuggest, API_METHODS.POST, requestBody, null, controller);
};

export const getHotelDetails = async (
  accessJWT: string,
  hotelId: string,
  controller: AbortController,
  isLoading?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const endpoint = endPoints.hotelDetail(hotelId);
  if (!hasUsableJWT(accessJWT, "getHotelDetails")) return null;
  try {
    if (isLoading) isLoading(true);
    const response = await fetchApi(accessJWT, endpoint, API_METHODS.GET, null, null, controller);
    if (isLoading) isLoading(false);
    return response;
  } catch (error) {
    if (isLoading) isLoading(false);
    return null;
  }
};

export interface iBookHotelAndFlightArgs {
  clientId: string;
  userId: string;
  paymentDetails: PaymentDetails;
  passengerDetails: PassengerDetail[];
  flight?: string;
  hotel?: iHotelRoomBookArgs[];
  flightFareId?: any;
  paymentId?: any;
  flightAvailabilityStatus?: string;
  // `flight` is a transactionId string, so the flight's supplyType rides as a sibling.
  flightSupplyType?: SupplyType;
}

export interface PassengerDetail {
  roomInfo?: any;
  paxType: string;
  gender?: string;
  leadPax: boolean;
  title?: any;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  countryCode: string;
  mobileNumber: string;
  dateOfBirth?: string;
  infantData?: InfantData | null | undefined;
  document?: Document;
  operationalReference?: OperationalReference;
  frequentFlyerinfo?: FrequentFlyerinfo;
  meals?: string[] | null;
  countryName?: string;
  iso?: string;
}

export interface Document {
  type: string | null;
  number: string | null;
  nationality: string | null;
  issuedCountry: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
}

export interface FrequentFlyerinfo {
  flyerNumber: string;
  frequentFlyerProgram: string;
  airline: string;
}

export interface InfantData {
  title: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  document: Document;
}

export interface OperationalReference {
  specialAssistance: string | null;
}

export interface PaymentDetails {
  successUrl: string;
  cancelUrl: string;
  amount: string;
  currency: string;
  remarks: string;
  paymentMethod?: PaymentMethod;
}

export const bookHotelAndFlight = async (
  accessJWT: string,
  args: iBookHotelAndFlightArgs,
  isLoading?: any
): Promise<any> => {
  const URL = `${ENDPOINT}/app/v1/itinerary/prebooking`;
  const config = getHeaderConfig(null, accessJWT);
  if (!hasUsableJWT(accessJWT, "bookHotelAndFlight")) return null;
  const sharedByUserId = getSharedByUserId();
  const body = {
    ...args,
    paymentDetails: withPaymentMethod(args.paymentDetails),
    ...(sharedByUserId ? { shared_by_user_id: sharedByUserId } : {}),
    ...getRequestedItineraryIdField(),
    ...getUserTimezoneField(),
  };
  try {
    if (isLoading) isLoading(true);
    consoleLog([body, "payload for prebooking"]);
    const encryptedBody = encryptData(body);
    const response = await axios.post(URL, encryptedBody, config);
    if (isLoading) isLoading(false);
    toast.dismiss();

    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog([parseData, "prebooking response"]);
      if (parseData?.statusCode === 200 && parseData?.data) {
        rememberBookingIdempotencyKey(parseData.data?.idempotencyKey);
        rememberRequestedItineraryId(parseData.data?.itineraryID);
        return parseData?.data;
      }
      return { __error: true, ...parseData };
    } else return null;
  } catch (error: any) {
    consoleLog([error, "prebooking Error"]);
    if (isLoading) isLoading(false);
    const statusCode = error?.response?.status ?? error?.response?.data?.statusCode ?? 500;
    const errData = error?.response?.data ? (() => { try { return JSON.parse(decryptData(error.response.data)); } catch { return null; } })() : null;
    return { __error: true, statusCode, message: errData?.message || error?.message, rawError: errData?.rawError, details: errData?.rawError?.details };
  }
};

interface iConfirmBookHotelAndFlightArgs {
  itineraryId: string;
  userId: string;
  id: string;
  paymentId?: string;
  paymentIntentId?: string;
}

export const confirmBookHotelAndFlight = async (
  accessJWT: string,
  args: iConfirmBookHotelAndFlightArgs
) => {
  const URL = `${ENDPOINT}/app/v1/itinerary/booking`;
  const config = getHeaderConfig(null, accessJWT);
  const body = {
    ...args,
    ...getUserTimezoneField(),
    ...getBookingIdempotencyKeyField(),
    ...getBrandTenantField(),
  };
  try {
    consoleLog(["API REQUEST", URL, body]);
    if (!hasUsableJWT(accessJWT, "confirmBookHotelAndFlight")) return null;
    const response = await axios.post(URL, encryptData(body), config);

    if (response?.data) {
      const decryptedData = decryptData(response?.data);
      const parseData = JSON.parse(decryptedData);
      consoleLog(["API RESPONSE", URL, parseData]);
      if (parseData?.statusCode !== 200) {
        captureException({
          error: "BOOKING CONFIRMATION ERROR parseData?.statusCode !== 200",
          response: response,
        });
        return null;
      }
      if (parseData?.data) return parseData?.data;
      captureException({
        error: "BOOKING CONFIRMATION ERROR NULL",
        response: response,
      });
      return null;
    } else {
      captureException({
        error: "BOOKING CONFIRMATION ERROR NO RESPONSE DATA",
        response: response,
      });
      return null;
    }
  } catch (error: any) {
    toast.dismiss();
    captureException({
      error: "BOOKING CONFIRMATION ERROR Catch",
      response: error,
    });
    return null;
  }
};

export const fetchCountries = async (accessJWT: string): Promise<iCountryDataResponse> => {
  const response = await fetchApi(accessJWT, endPoints.county, API_METHODS.GET);
  if (Array.isArray(response?.data)) {
    response.data = response.data.filter(
      (country: iCountryData) => !/[a-zA-Z]/.test(country?.code ?? "")
    );
  }
  return response;
};

export const getHotelReviews = async (
  accessJWT: string,
  userId: string,
  hotelId: string,
  offset: number,
  count: number,
  controller?: AbortController
) => {
  if (isWidgetRuntime()) {
    return callWidgetTool(MCP_WIDGET_TOOLS.reviews, withoutUserId({ userId, hotelId, offset, count }), controller?.signal);
  }
  consoleLog(["API REQUEST", endPoints.hotelReviews, { userId, hotelId, offset, count }]);
  return await fetchApi(
    accessJWT,
    endPoints.hotelReviews,
    API_METHODS.POST,
    {
      userId,
      hotelId,
      offset,
      count,
    },
    null,
    controller
  );
};

export const fetchFlightPreference = async (
  accessJWT: string,
  searchKey: string,
  userId: string
): Promise<AirlineApiResponse> => {
  const endpoint = endPoints.getAirlinePreference;
  return await fetchApi(accessJWT, endpoint, API_METHODS.POST, { searchQuery: searchKey, userId: userId });
};
export const fetchHotelBrands = async (
  accessJWT: string,
  searchKey: string,
  userId: string
): Promise<HotelBrandApiResponse> => {
  const endpoint = endPoints.getHotelBrands;
  return await fetchApi(accessJWT, endpoint, API_METHODS.POST, { searchQuery: searchKey, userId: userId });
};

export const fetchFairDetails = async (
  accessJWT: string,
  encryptedKey: string,
  uniqueId: string,
  controller: AbortController,
  userId: string,
  version: "v2" | "v3" = getPickSource("flight") === "chat" ? "v3" : "v2"
) => {
  const requestBody = {
    version,
    id: `${encryptedKey}-${uniqueId}`,
    userId: userId,
    ...supplyTypeField(getFlightSupplyType(encryptedKey)),
  };

  if (isWidgetRuntime()) {
    return callWidgetTool(MCP_WIDGET_TOOLS.fares, withoutUserId(requestBody), controller.signal);
  }
  return await fetchApi(accessJWT, endPoints.fareDetails, API_METHODS.POST, requestBody, null, controller);
};

export const applyPromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.promoCode, API_METHODS.POST, withPaymentMethod(requestBody), null, controller);
};

export const removePromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.removePromoCode, API_METHODS.POST, withPaymentMethod(requestBody), null, controller);
};

export const changePromoCode = async (accessJWT: string, requestBody: any, controller: AbortController) => {
  return await fetchApi(accessJWT, endPoints.changePromoCode, API_METHODS.POST, withPaymentMethod(requestBody), null, controller);
};
